/**
 * Password Validation Utility
 * Provides strength scoring and advisory hints for passwords.
 * Server-side enforcement (min 12 chars) is handled by Supabase dashboard config.
 */

export type PasswordStrength = 'weak' | 'fair' | 'strong';

export interface PasswordValidationResult {
  strength: PasswordStrength;
  score: number;
  messages: string[];
  isAcceptable: boolean;
}

const CRITERIA: ReadonlyArray<{
  test: (pw: string) => boolean;
  message: string;
}> = [
  { test: (pw) => pw.length >= 10, message: 'At least 10 characters' },
  { test: (pw) => /[A-Z]/.test(pw), message: 'An uppercase letter' },
  { test: (pw) => /[a-z]/.test(pw), message: 'A lowercase letter' },
  { test: (pw) => /[0-9]/.test(pw), message: 'A digit' },
  { test: (pw) => /[^A-Za-z0-9]/.test(pw), message: 'A special character' },
];

function scoreToStrength(score: number): PasswordStrength {
  if (score >= 5) return 'strong';
  if (score >= 3) return 'fair';
  return 'weak';
}

export function validatePassword(password: string): PasswordValidationResult {
  const unmet = CRITERIA.filter((c) => !c.test(password));
  const score = CRITERIA.length - unmet.length;

  return {
    strength: scoreToStrength(score),
    score,
    messages: unmet.map((c) => c.message),
    isAcceptable: score >= 3,
  };
}
